import { Grid } from '@mui/material';
import { RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import GeoFences from '../../../assets/svg/onboarding/icons/GeoFences';
import TimeWindows from '../../../assets/svg/onboarding/icons/TimeWindows';
import Volume from '../../../assets/svg/onboarding/icons/Volume';
import Weight from '../../../assets/svg/onboarding/icons/Weight';
import { Project } from '../../../domain/project/Project';
import { useUpdateProject } from '../../../hooks/project/useUpdateProject';
import Button from '../../../react-components/Button';
import Text from '../../../react-components/Text';
import eventsPanel from '../../../services/events';
import { AnimatedStep } from './AnimatedStep';

const availableConstraints = [`weight`, `volume`, `time_windows`, `geo_fences`];

interface ConstraintsProps {
  project: Project;
  onNextStep: () => void;
}

export const Constraints = ({ project, onNextStep }: ConstraintsProps) => {
  const { success, run: updateProject } = useUpdateProject({ project });
  const [constraints, setConstraints] = useState<string[]>([]);

  useEffect(() => {
    if (success) {
      setTimeout(() => {
        onNextStep();
        eventsPanel.onboardingPageView({ page: `constraints`, data: { constraints } });
      }, 500);
    }
  });

  const isConstraintActive = (c: string) => constraints.includes(c);

  const getIconByConstraint = (c: string) => {
    switch (c) {
      case `weight`:
        return (
          <Weight
            style={{ width: `20px` }}
            color={isConstraintActive(c) ? RoutalPalette.primary40 : RoutalPalette.text}
          />
        );
      case `volume`:
        return (
          <Volume
            style={{ width: `20px` }}
            color={isConstraintActive(c) ? RoutalPalette.primary40 : RoutalPalette.text}
          />
        );
      case `time_windows`:
        return (
          <TimeWindows
            style={{ width: `20px` }}
            color={isConstraintActive(c) ? RoutalPalette.primary40 : RoutalPalette.text}
          />
        );
      case `geo_fences`:
        return (
          <GeoFences
            style={{ width: `20px` }}
            color={isConstraintActive(c) ? RoutalPalette.primary40 : RoutalPalette.text}
          />
        );
      default:
        return null;
    }
  };

  return (
    <AnimatedStep id="constraintsAnimatedContainer" isVisible={!success}>
      <Grid
        container
        direction="column"
        wrap="nowrap"
        style={{ padding: `20px`, maxWidth: `660px`, position: `relative` }}
      >
        <Grid item>
          <Text variant="h6" intlId="company.onboarding.constraints.info" />
        </Grid>

        <VSpacer medium />

        <Grid item container direction="column" wrap="nowrap">
          {availableConstraints.map((c, index) => (
            <Grid
              key={c}
              item
              container
              direction="row"
              wrap="nowrap"
              alignItems="center"
              justifyContent="center"
              style={{
                cursor: `pointer`,
                borderRadius: `10px`,
                padding: `12px`,
                height: `76px`,
                border: isConstraintActive(c)
                  ? `2px solid ${RoutalPalette.primary40}`
                  : `1px solid ${RoutalPalette.neutral10}`,
                marginBottom: index < availableConstraints.length - 1 ? `12px` : `0px`,
              }}
              onClick={() => {
                if (isConstraintActive(c)) {
                  setConstraints(constraints.filter((constraint) => constraint !== c));
                } else {
                  setConstraints([...constraints, c]);
                }
              }}
            >
              <Grid item container direction="row" style={{ width: `unset`, marginRight: `20px` }}>
                {getIconByConstraint(c)}
              </Grid>

              <Grid item container direction="column" wrap="nowrap">
                <Grid item>
                  <Text
                    intlId={`company.onboarding.constraints.option.${c}`}
                    style={{
                      cursor: `pointer`,
                      color: isConstraintActive(c) ? RoutalPalette.primary40 : RoutalPalette.text,
                    }}
                  />
                </Grid>

                {isConstraintActive(c) && (
                  <>
                    <Grid item>
                      <VSpacer small />
                    </Grid>
                    <Grid item>
                      <Text
                        variant="label"
                        intlId={`company.onboarding.constraints.option.${c}.info`}
                        style={{ cursor: `pointer`, marginRight: `20px` }}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>
          ))}
        </Grid>

        <Grid
          item
          container
          direction="row"
          wrap="nowrap"
          alignItems="center"
          justifyContent="center"
          style={{ position: `absolute`, bottom: `-60px` }}
        >
          <Button
            intlId="company.onboarding.next"
            style={{
              width: `200px`,
              height: `32px`,
            }}
            onClick={() => {
              availableConstraints.forEach((availableConstraint) => {
                (project.view.constraints as any)[availableConstraint] = constraints.includes(availableConstraint);
              });

              updateProject({
                view: project.view,
              });
            }}
          />
        </Grid>
      </Grid>
    </AnimatedStep>
  );
};
